import { REMUNERACION_TIERS, RemuneracionTier, RemuneracionZone } from './remuneracionTiers';
import { calculatePaymentDueDate, getSettlementStatus, SettlementStatus } from './paymentDueDate';

/**
 * Cálculo on-the-fly de la liquidación NZ (zonas de entrada), sin persistencia:
 * se reprocesa la serie completa de sesiones settled por mes cada vez.
 * Reutiliza REMUNERACION_TIERS (misma tabla que consume MetricsLiquidacionTab.tsx en la UI)
 * como única fuente de verdad de rangos/montos — no se duplica la tabla acá.
 */

export interface MonthlySettledSessions {
  /** Formato 'YYYY-MM'. Se asume orden cronológico ascendente, arrancando en el primer mes
   *  del esquema NZ (julio 2026) con acumulado en cero — el caller es responsable de recortar
   *  la serie a partir de ese mes. */
  month: string;
  sessionsCount: number;
}

export interface NZSettlementMonthResult {
  month: string;
  sessionsThisMonth: number;
  /** Sesiones acumuladas de meses anteriores que todavía no se liquidaron, antes de sumar este mes. */
  carriedOverFromPreviousMonths: number;
  /** carriedOverFromPreviousMonths + sessionsThisMonth */
  totalConsideredForThreshold: number;
  /** Tier de REMUNERACION_TIERS alcanzado este mes, o null si no se llegó al umbral mínimo. */
  tier: RemuneracionTier | null;
  crossedThreshold: boolean;
  /** Monto numérico a liquidar este mes (parseado de tier.amount), null si no cruzó el umbral. */
  amountToSettle: number | null;
  /** Acumulado que pasa al mes siguiente (0 si este mes liquidó y el contador se reinició). */
  remainingCarryOver: number;
  /** Meses consecutivos (incluyendo este) sin cruzar el umbral mínimo. Se reinicia a 0 al liquidar. */
  monthsWithoutThreshold: number;
  /** true una vez que pasaron 2 meses completos sin cruzar el umbral — habilita pedir liquidación
   *  anticipada. La acción de solicitarla se resuelve en la UI; acá solo es el estado calculado. */
  earlyLiquidationEligible: boolean;
  /** $50 cada 5 sesiones acumuladas, informativo — solo tiene sentido si earlyLiquidationEligible es true. */
  earlyLiquidationAmount: number;
  /** Día 5 calendario del mes siguiente, corrido al próximo día hábil si cae en fin de semana o
   *  feriado nacional argentino (ver paymentDueDate.ts). */
  dueDate: Date;
  /** 'pending' si hoy es anterior a dueDate, 'paid' si es igual o posterior (sin verificar
   *  acreditación real — simplificación aceptada para V1). */
  status: SettlementStatus;
}

/**
 * Mes de inicio del esquema NZ ('YYYY-MM'). El acumulado arranca en cero en este mes —
 * cualquier serie de sesiones armada para calculateNZSettlementSchedule debe empezar acá.
 */
export const NZ_SETTLEMENT_SCHEME_START_MONTH = '2026-07';

// Acá vivía `isNZPsychologist(rankingCategory)`. Se eliminó a propósito: comparar
// `rankingCategory` contra 'NZ' para decidir qué se le muestra a la psicóloga es un segundo
// criterio en paralelo al gate central (ProfileMetricsTab → journeyMode → prop `isNZ`), y es
// justo lo que arregló el commit 4e380d1. Además ignora `ranking_anterior` (recién graduadas)
// y decide con `undefined` si el contexto todavía no cargó. Si necesitás saber si es NZ,
// tomalo de la prop que baja del gate central, no de acá.

interface TierRange {
  tier: RemuneracionTier;
  min: number;
  max: number; // Infinity para el tier superior abierto ('130+')
}

const EARLY_LIQUIDATION_SESSIONS_BLOCK = 5;
const EARLY_LIQUIDATION_AMOUNT_PER_BLOCK = 50;
const MONTHS_WITHOUT_THRESHOLD_FOR_EARLY_LIQUIDATION = 2;

/** '$1.030' -> 1030, '$170' -> 170 (formato AR: '.' como separador de miles). */
function parseTierAmount(amount: string): number {
  const digitsOnly = amount.replace(/[^0-9]/g, '');
  return digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
}

/** '15 – 19' -> [15, 19], '130+' -> [130, Infinity]. */
function parseTierRange(range: string): [number, number] {
  const trimmed = range.trim();
  if (trimmed.endsWith('+')) {
    const min = parseInt(trimmed.slice(0, -1), 10);
    return [min, Infinity];
  }
  const [minStr, maxStr] = trimmed.split('–').map((s) => s.trim());
  return [parseInt(minStr, 10), parseInt(maxStr, 10)];
}

function buildTierRanges(tiers: RemuneracionTier[]): TierRange[] {
  return tiers.map((tier) => {
    const [min, max] = parseTierRange(tier.range);
    return { tier, min, max };
  });
}

const TIER_RANGES = buildTierRanges(REMUNERACION_TIERS);

/** Umbral mínimo derivado del primer tier de REMUNERACION_TIERS (hoy 15), en vez de repetir el
 *  número mágico — si la tabla de tarifas cambia, el umbral se ajusta solo. */
export const NZ_MIN_THRESHOLD = TIER_RANGES.length > 0 ? TIER_RANGES[0].min : 0;

function findTierForSessionsCount(count: number): RemuneracionTier | null {
  const match = TIER_RANGES.find(({ min, max }) => count >= min && count <= max);
  return match ? match.tier : null;
}

export function getRemuneracionZoneForSessionsCount(count: number): RemuneracionZone | null {
  return findTierForSessionsCount(count)?.zone ?? null;
}

/**
 * Procesa la serie mensual de sesiones settled de una psicóloga NZ (en orden cronológico,
 * arrancando en el mes en que el acumulado es cero) y devuelve, para cada mes: sesiones del
 * mes, acumulado arrastrado, franja/monto liquidado (con reset del contador al cruzar), y el
 * flag de elegibilidad para liquidación anticipada tras 2 meses sin cruzar el umbral mínimo.
 */
export function calculateNZSettlementSchedule(
  monthlySessions: MonthlySettledSessions[],
  today: Date = new Date()
): NZSettlementMonthResult[] {
  let carryOver = 0;
  let monthsWithoutThreshold = 0;

  return monthlySessions.map(({ month, sessionsCount }) => {
    const totalConsideredForThreshold = carryOver + sessionsCount;
    const tier = findTierForSessionsCount(totalConsideredForThreshold);
    const crossedThreshold = tier !== null;

    const amountToSettle = crossedThreshold ? parseTierAmount(tier.amount) : null;
    const remainingCarryOver = crossedThreshold ? 0 : totalConsideredForThreshold;

    monthsWithoutThreshold = crossedThreshold ? 0 : monthsWithoutThreshold + 1;

    const earlyLiquidationEligible =
      monthsWithoutThreshold >= MONTHS_WITHOUT_THRESHOLD_FOR_EARLY_LIQUIDATION;
    const earlyLiquidationAmount =
      Math.floor(totalConsideredForThreshold / EARLY_LIQUIDATION_SESSIONS_BLOCK) *
      EARLY_LIQUIDATION_AMOUNT_PER_BLOCK;

    const [closedYear, closedMonthNum] = month.split('-').map(Number);
    const closedMonth = new Date(closedYear, closedMonthNum - 1, 1);
    const dueDate = calculatePaymentDueDate(closedMonth);
    const status = getSettlementStatus(dueDate, today);

    const result: NZSettlementMonthResult = {
      month,
      sessionsThisMonth: sessionsCount,
      carriedOverFromPreviousMonths: carryOver,
      totalConsideredForThreshold,
      tier,
      crossedThreshold,
      amountToSettle,
      remainingCarryOver,
      monthsWithoutThreshold,
      earlyLiquidationEligible,
      earlyLiquidationAmount,
      dueDate,
      status,
    };

    carryOver = remainingCarryOver;

    return result;
  });
}
